import DOMPurify from "dompurify";

/**
 * The single HTML sanitisation boundary for this app.
 *
 * This is the boilerplate copy: every repo forked from here inherits it, and
 * it is the file to change when the allowlists need to move fleet-wide.
 *
 * Everything rendered through `dangerouslySetInnerHTML` must pass through one
 * of the helpers below. Two sources of untrusted HTML reach the DOM here:
 *
 *   1. Supplier / AliExpress product descriptions (`IProduct.description`) —
 *      authored outside Spocket and relayed by the API.
 *   2. Plan feature strings from the billing API, rendered on every pricing
 *      and checkout surface.
 *
 * Both used to be injected raw. Because tokens live in localStorage, script
 * execution in this origin is full account takeover, so these are hard
 * boundaries rather than cosmetic cleanup.
 *
 * Never hand-roll sanitisation with string or regex manipulation: a regex sees
 * text, the browser sees a parser, and the two disagree in ways attackers
 * rely on. Add to the allowlists here instead.
 */

/**
 * Rich product descriptions: block layout, tables, images and links are
 * legitimate formatting a supplier may use.
 */
const PRODUCT_ALLOWED_TAGS = [
  "a", "abbr", "b", "blockquote", "br", "caption", "code", "div", "em",
  "h1", "h2", "h3", "h4", "h5", "h6", "hr", "i", "img", "li", "ol", "p",
  "pre", "small", "span", "strike", "strong", "sub", "sup", "table",
  "tbody", "td", "tfoot", "th", "thead", "tr", "u", "ul",
];

const PRODUCT_ALLOWED_ATTR = [
  "alt", "class", "colspan", "height", "href", "referrerpolicy", "rel",
  "rowspan", "src", "target", "title", "width",
];

/**
 * Only http(s) and mailto. This is what blocks `javascript:` hrefs and
 * `data:text/html` payloads, which are script execution in link form.
 */
const SAFE_URI_REGEXP = /^(?:https?|mailto):/i;

/**
 * Short plan-feature and label strings ("250 products", "<b>Unlimited</b>").
 *
 * Deliberately far tighter than the product allowlist: these are one-line
 * labels that render on pricing and checkout screens, so inline emphasis is
 * all they need. No links, no images, no block elements — an injected link or
 * image on a checkout screen is a phishing surface, not a formatting choice.
 */
const INLINE_ALLOWED_TAGS = [
  "b", "br", "em", "i", "s", "small", "span", "strike", "strong", "sub",
  "sup", "u",
];

const INLINE_ALLOWED_ATTR = ["class"];

/**
 * Both sanitisers bind their DOMPurify instance lazily rather than using the
 * module-level default export. The default export is only a usable instance if
 * a `window` existed when the module was first evaluated; binding on first call
 * instead keeps this working regardless of import order.
 *
 * Product descriptions get their *own* instance because the hooks below must
 * apply only to them — DOMPurify hooks are registered per instance, so sharing
 * one would leak the img/anchor rewrites onto plan-feature strings too.
 */
let productPurifier: typeof DOMPurify | null = null;
let inlinePurifier: typeof DOMPurify | null = null;

const createPurifier = (): typeof DOMPurify => DOMPurify(window);

const getProductPurifier = (): typeof DOMPurify => {
  if (productPurifier) return productPurifier;

  const instance = createPurifier();

  // Supplier images are hotlinked from third-party CDNs. Stripping the
  // referrer keeps merchant URLs out of their logs. Done as a hook so the
  // rewrite runs on the parsed DOM node rather than on an HTML string.
  instance.addHook("afterSanitizeAttributes", (node) => {
    if (node.nodeName === "IMG") {
      node.setAttribute("referrerpolicy", "no-referrer");
    }

    // Any surviving link opens off-site; deny it access to window.opener.
    if (node.nodeName === "A" && node.hasAttribute("href")) {
      node.setAttribute("rel", "noopener noreferrer");
    }
  });

  productPurifier = instance;
  return instance;
};

const getInlinePurifier = (): typeof DOMPurify => {
  if (!inlinePurifier) inlinePurifier = createPurifier();
  return inlinePurifier;
};

type ProductSanitizeOptions = {
  /**
   * Drop <img> entirely. Used by the import-list description editors in the
   * product repos, where supplier images must not be carried into the
   * description stored on the merchant's own Shopify product.
   */
  stripImages?: boolean;
};

export const sanitizeProductDescriptionHtml = (
  html: string | null | undefined,
  { stripImages = false }: ProductSanitizeOptions = {}
): string => {
  if (html == null || html === "") return "";

  return getProductPurifier().sanitize(String(html), {
    ALLOWED_TAGS: stripImages
      ? PRODUCT_ALLOWED_TAGS.filter((tag) => tag !== "img")
      : PRODUCT_ALLOWED_TAGS,
    ALLOWED_ATTR: PRODUCT_ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: SAFE_URI_REGEXP,
  });
};

export const sanitizeInlineHtml = (
  html: string | null | undefined
): string => {
  if (html == null || html === "") return "";

  return getInlinePurifier().sanitize(String(html), {
    ALLOWED_TAGS: INLINE_ALLOWED_TAGS,
    ALLOWED_ATTR: INLINE_ALLOWED_ATTR,
  });
};
